import moment from 'moment';
import 'moment/locale/de';
import Timeline, { DateHeader, TimelineHeaders } from "react-calendar-timeline";
import 'react-calendar-timeline/style.css';
import './PlanningScheduler.css';
import { useMemo, useState } from 'react';
import addZeroToTime from '../../../../sharedComponents/addZeroToTime';
import LoadingSpinner from '../../../../sharedComponents/LoadingSpinner/LoadingSpinner';

function PlanningScheduler(props) {
    moment.locale('de');

    const [items, setItems] = useState([]);
    const [groups, setGroups] = useState([]);
    
    useMemo(() => {
        setGroups(props.employees
            .filter(employee => 
                props.selectedArea === null || 
                props.selectedArea.assigned_employee_ids.includes(employee.id)
            )
            .map(employee => ({
                id: employee.id,
                title: `${employee.first_name} ${employee.last_name}`
            })));
    }, [props.employees, props.selectedArea]);   

    useMemo(() => {
        const newItems = [];        
        const currentDate = moment(props.currentDate).startOf('day');
        
        // Filter scheduling time items for current day only
        props.employeesSchedulingTime.forEach(element => {
            const itemDate = moment(new Date(element.date)).startOf('day');            
            
            // Only add items that match the current date
            if (itemDate.isSame(currentDate)) {                
                newItems.push({
                    id: element.id,
                    group: element.employee_id,
                    title: "", 
                    start_time: moment(element.date + " " + element.start_time), 
                    end_time: moment(element.date + " " + element.end_time),
                    canMove: true,
                    canResize: true,
                });
            }
        });

        const dayOfWeekMap = {
            'SUNDAY': 0,
            'MONDAY': 1,
            'TUESDAY': 2,
            'WEDNESDAY': 3,
            'THURSDAY': 4,
            'FRIDAY': 5,
            'SATURDAY': 6
        };

        props.employees.forEach(employee => {
            const corePolicy = props.employeesCoreWorkTime.find(
                core => core.id === employee.assigned_core_working_hours_policy_id
            );
            
            if (corePolicy) {
                corePolicy.core_working_hours_periods.forEach(period => {
                    const dayOfWeek = dayOfWeekMap[period.day_of_week];
                    
                    if (dayOfWeek === undefined) {
                        console.warn(`Unknown day of week: ${period.day_of_week}`);
                        return;
                    }

                    const currentDate = moment(props.currentDate);
                    const targetDate = currentDate.clone().day(dayOfWeek);
                    
                    // Only add core hours for the current day
                    if (!targetDate.isSame(currentDate, 'day')) {
                        return;
                    }

                    const startTime = targetDate.clone()
                        .set({
                            hour: parseInt(period.start_time.split(':')[0]),
                            minute: parseInt(period.start_time.split(':')[1]),
                            second: parseInt(period.start_time.split(':')[2] || 0)
                        });
                    
                    const endTime = targetDate.clone()
                        .set({
                            hour: parseInt(period.end_time.split(':')[0]),
                            minute: parseInt(period.end_time.split(':')[1]),
                            second: parseInt(period.end_time.split(':')[2] || 0)
                        });

                    // Check if item already exists for this day and employee
                    const existingItem = newItems.find(
                        item => item.group === employee.id && 
                        moment(item.start_time).isSame(targetDate, 'day')
                    );

                    if (!existingItem) {
                        newItems.push({
                            id: "core." + employee.id + period.id,
                            group: employee.id,
                            title: corePolicy.name, 
                            start_time: startTime, 
                            end_time: endTime,
                            canMove: true,
                            canResize: true,
                        });
                    }
                });
            }
        });
        
        setItems(newItems);
    }, [props.employeesSchedulingTime, props.employeesCoreWorkTime, props.employees, props.currentDate]);

    const start = moment(props.currentDate).startOf('day');
    const end = moment(props.currentDate).add(1, 'day').startOf('day');   

    const handleItemMove = (itemId, dragTime, newGroupOrder) => {
        const group = groups[newGroupOrder];
        const movedItem = items.find(item => item.id === itemId);

        if (!movedItem) return;
        
        const originalStart = moment(movedItem.start_time);
        const originalEnd = moment(movedItem.end_time);
        const newStarttimeMoment = moment(dragTime);
        const duration = moment(movedItem.end_time).diff(moment(movedItem.start_time));
        const newEndTimeMoment = moment(dragTime).add(duration);

        if (movedItem.group !== group.id) {
            console.log("Cannot move items between employees");
            return;
        }

        if (!originalStart.isSame(newStarttimeMoment, 'day') || !originalEnd.isSame(newEndTimeMoment, 'day')){
            console.log("Cannot move items to different days");
            return
        }
            
        
        setItems(prevItems => {
            return prevItems.map(item => {
                if (item.id === itemId) {
                    return {
                        ...item,
                        start_time: newStarttimeMoment,
                        end_time: newEndTimeMoment,
                    };
                }
                return item;
            });
        });
        
        const newStartDateObj = new Date(newStarttimeMoment.toDate());
        const newEndDateObj = new Date(newEndTimeMoment.toDate());
        const newDateString = newStartDateObj.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const newStartTime = addZeroToTime(newStartDateObj.getHours()) + ':' + addZeroToTime(newStartDateObj.getMinutes());
        const newEndTime = addZeroToTime(newEndDateObj.getHours()) + ':' + addZeroToTime(newEndDateObj.getMinutes());

        const localChangeId = movedItem.id.includes("core") ? false : movedItem.id;

        props.setLocalChanges(prevState => ({
            ...prevState,
            [`${group.id}-${newDateString}`]: {
                employee_id: group.id,
                date: newDateString,
                start_time: newStartTime,
                end_time: newEndTime,
                id: localChangeId,
                toDelete: false,
                comment: "",
                absent: false,
            }
        }));
    };

    const handleItemResize = (itemId, time, edge) => {
        const resizedItem = items.find(item => item.id === itemId);
        if (!resizedItem) return;

        const newTime = moment(time);
        const originalStartTime = moment(resizedItem.start_time);
        const originalEndTime = moment(resizedItem.end_time);

        const newStartTime = edge === "left" ? newTime : originalStartTime;
        const newEndTime = edge === "left" ? originalEndTime : newTime;

        // Check if resize would cross day boundaries
        if (!newStartTime.isSame(newEndTime, 'day')) {
            console.log("Cannot resize items across multiple days");
            return;
        }

        // Check if the day changed from original
        if (!newStartTime.isSame(originalStartTime, 'day') || !newEndTime.isSame(originalEndTime, 'day')) {
            console.log("Cannot move items to different days");
            return;
        }

        setItems(prevItems =>
            prevItems.map(item =>
                item.id === itemId
                    ? {
                        ...item,
                        start_time: newStartTime,
                        end_time: newEndTime
                    }
                    : item
            )
        );
        
        const newStartDateObj = new Date(newStartTime.toDate());
        const newEndDateObj = new Date(newEndTime.toDate());
        const newDateString = newStartDateObj.toLocaleDateString('de-DE', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
        });
        const newStartTimeString = addZeroToTime(newStartDateObj.getHours()) + ':' + addZeroToTime(newStartDateObj.getMinutes());
        const newEndTimeString = addZeroToTime(newEndDateObj.getHours()) + ':' + addZeroToTime(newEndDateObj.getMinutes());

        const localChangeId = resizedItem.id.includes("core") ? false : resizedItem.id;

        props.setLocalChanges(prevState => ({
            ...prevState,
            [`${resizedItem.group}-${newDateString}`]: {
                employee_id: resizedItem.group,
                date: newDateString,
                start_time: newStartTimeString,
                end_time: newEndTimeString,
                id: localChangeId,
                toDelete: false,
                comment: "",
                absent: false,
            }
        }));
    };

    const germanDateHeaderFormat = ([timeStart]) => {        
        return new Date(timeStart).toLocaleDateString('de-DE', { 
            weekday: "long",
            day: '2-digit', 
            month: 'long', 
            year: 'numeric' 
        });
    };

    return props.isLoading ? <LoadingSpinner /> : 
        <div className="worktime-planning-timeline">
            <Timeline
                groups={groups}
                items={items}
                visibleTimeStart={start.valueOf()}
                visibleTimeEnd={end.valueOf()}
                onItemMove={handleItemMove}
                onItemResize={handleItemResize}
                defaultTimeStart={start}
                defaultTimeEnd={end}
            >
                <TimelineHeaders>
                    <DateHeader unit='primaryHeader' 
                        labelFormat={germanDateHeaderFormat}
                    />
                    <DateHeader />
                </TimelineHeaders>
            </Timeline>
        </div>
}

export default PlanningScheduler;